import type { ReactElement } from 'react';
import { PostCustomers } from './PostCustomers';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { Sheet } from './Sheet';
import { Skeleton } from './Skeleton';
import { ReceiptRow } from './ReceiptRow';
import { PublishFailure } from './PublishFailure';
import { Icons } from '@/shared/constants/icons';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { usePost } from '@/shared/hooks/usePosts';
import { buildReceipt, receiptHeadline, verifiedLine } from '@/shared/lib/receipt';
import { platformName, postRetryIsSafe } from '@/shared/lib/post-result-error';

interface ReceiptSheetProps {
  postId: string | null;
  onClose: () => void;
}

/** The check that draws itself (`panel` timing), inside a `--good-soft` circle. */
function DrawnCheck(): ReactElement {
  const reduced = useReducedMotion() ?? false;
  return (
    <span className="w-14 h-14 rounded-full inline-flex items-center justify-center bg-[var(--good-soft)]" data-testid="receipt-check">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--good)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <motion.path d="M5 12.5l4.5 4.5L19 7" initial={reduced ? false : { pathLength: 0 }} animate={{ pathLength: 1 }} transition={motionSafe(TRANSITION.panel, reduced)} />
      </svg>
    </span>
  );
}

/**
 * A post's receipt (Task 1.3): how many channels it went out on, one row per
 * channel in the order the post lists them with a link to each live post,
 * the failures in their usual words, and what was actually posted. A sheet
 * from the side on a desktop, from the bottom on a phone.
 */
export function ReceiptSheet({ postId, onClose }: ReceiptSheetProps): ReactElement {
  const { t } = useTranslation();
  const { data: post, isLoading, isError, refetch } = usePost(postId ?? undefined);
  const receipt = post ? buildReceipt(post) : null;
  const allOut = receipt !== null && receipt.total > 0 && receipt.published === receipt.total && receipt.gone.length === 0;

  return (
    <Sheet open={postId !== null} onClose={onClose} id="receipt" title={t('receipt.title')}>
      {isLoading && (
        <div aria-busy="true" className="flex flex-col gap-3">
          <Skeleton height={56} width={56} />
          <Skeleton height={22} width="70%" />
          <Skeleton height={44} />
          <Skeleton height={44} />
        </div>
      )}
      {isError && !isLoading && (
        <div className="card flex flex-col gap-2 p-4" role="alert">
          <p className="m-0 text-body text-[var(--ink)]">{t('receipt.loadFailed')}</p>
          <button type="button" className="btn btn-secondary btn-sm w-fit" onClick={() => void refetch()}>{t('receipt.tryAgain')}</button>
        </div>
      )}
      {post && receipt && (
        <div className="flex flex-col gap-4" data-testid="receipt">
          <div className="flex flex-col items-start gap-3">
            {allOut ? (
              <DrawnCheck />
            ) : (
              <span
                className={`w-14 h-14 rounded-full inline-flex items-center justify-center ${receipt.published > 0 ? 'bg-[var(--warn-soft)] text-[var(--warn)]' : 'bg-[var(--bad-soft)] text-[var(--bad)]'}`}
              >
                <Icons.warning size={26} aria-hidden="true" />
              </span>
            )}
            <h3 className="serif m-0 text-[24px] leading-tight text-[var(--ink)]" aria-live="polite">{receiptHeadline(receipt, t)}</h3>
          </div>

          <ul className="list-none m-0 p-0">
            {receipt.lines.map((line) => (
              <ReceiptRow
                key={line.platform}
                platform={line.platform}
                state={line.state}
                publishedAt={line.result?.publishedAt}
                url={line.result?.platformPostUrl}
                verified={line.result ? verifiedLine(line.result, t) : null}
                gone={line.result?.verification === 'gone'}
              />
            ))}
          </ul>

          {receipt.failed.length > 0 && (
            <div className="rounded-[var(--r-md)] px-3 py-2.5 flex flex-col gap-3 bg-[var(--bad-soft)]">
              {receipt.failed.map((r) => <PublishFailure key={r.platform} postId={post.id} result={r} retrySafe={postRetryIsSafe(post.results)} />)}
            </div>
          )}

          {/* What the post brought (Task 3.3); nothing while insights.customers is off. */}
          {receipt.published > 0 && <PostCustomers postId={post.id} />}
          {receipt.published > 0 && (
            <details className="text-ui">
              <summary className="cursor-pointer select-none font-semibold text-[var(--ink-2)] w-fit">{t('receipt.whatWasPosted')}</summary>
              <div className="flex flex-col gap-3 mt-2">
                {receipt.lines.filter((l) => l.state === 'published').map((l) => (
                  <div key={l.platform}>
                    <div className="text-meta font-semibold text-[var(--ink-3)]">{platformName(l.platform)}</div>
                    <p className="m-0 mt-0.5 whitespace-pre-wrap break-words text-[var(--ink)]" dir="auto">{l.result?.formattedContent ?? post.content}</p>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Sheet>
  );
}
